import { type ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useNavigate } from '@remix-run/react';

import useToast, { type ToastData } from '~/hooks/useToast';

type Props = {
  children: ({ isLoading, submit }: { isLoading: boolean; submit: (priceId: string) => void }) => ReactNode;
  onSuccess?: () => void;
  tierId?: string | null;
  subscriberId?: string;
};

const ChangePlanAction = ({ children, onSuccess, tierId, subscriberId }: Props) => {
  const { t } = useTranslation('', { keyPrefix: 'billing.plans_modal' });
  const navigate = useNavigate();
  const fetcher = useFetcher<{ toast?: ToastData }>();
  const fetcherData = fetcher.data;
  const isLoading = fetcher.state === 'submitting';

  useToast(fetcherData?.toast, onSuccess);

  useEffect(() => {
    if (fetcherData?.toast?.status === 'success' && subscriberId) {
      navigate(`/subscribe/${subscriberId}/billing`);
    }
  }, [fetcherData?.toast, navigate, subscriberId]);

  const submit = useCallback(
    (priceId: string) => {
      const formData = new FormData();
      formData.append('price_id', priceId);
      if (tierId) formData.append('tier_id', tierId);
      formData.append('update_subscription_success_message', t('toast.success'));
      formData.append('update_subscription_error_message', t('toast.error'));
      fetcher.submit(formData, { method: 'patch', action: '/update_subscription' });
    },
    [fetcher, tierId, t],
  );

  return children({ isLoading, submit });
};

export default ChangePlanAction;
